import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";

const EMPTY_FORM = { name: "", description: "", price: "", quantity: "25", status: 1 };

export function PlanFormDialog({ open, onOpenChange, plan, onSaved }) {
  const isEdit = Boolean(plan);
  const [form, setForm] = useState(EMPTY_FORM);
  const [items, setItems] = useState([]); // [{item_id, quantity, is_optional, is_default}]
  const [itemOptions, setItemOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get("/items", { params: { limit: 500 } }).then(({ data }) => setItemOptions(data.items)).catch(() => {});
    setErrors({});
    if (isEdit) {
      setLoadingDetail(true);
      apiClient.get(`/plans/${plan.id}`)
        .then(({ data }) => {
          setForm({ name: data.name || "", description: data.description || "", price: data.price || "", quantity: String(data.quantity ?? ""), status: data.status ?? 1 });
          setItems((data.items || []).map((it) => ({
            item_id: String(it.item_id), quantity: String(it.quantity ?? "1"),
            is_optional: Boolean(it.is_optional), is_default: Boolean(it.is_default),
          })));
        })
        .catch((error) => toast.error(getApiErrorMessage(error, "Could not load plan details.")))
        .finally(() => setLoadingDetail(false));
    } else {
      setForm(EMPTY_FORM);
      setItems([]);
    }
  }, [open, plan, isEdit]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addItemRow = () => setItems((rows) => [...rows, { item_id: "", quantity: "1", is_optional: false, is_default: true }]);
  const removeItemRow = (idx) => setItems((rows) => rows.filter((_, i) => i !== idx));
  const setItemField = (idx, key, value) => setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = {
      name: form.name, description: form.description,
      price: Number(form.price) || 0, quantity: Number(form.quantity) || 0,
      status: Number(form.status),
      items: items.filter((r) => r.item_id).map((r) => ({
        item_id: Number(r.item_id), quantity: Number(r.quantity) || 1,
        is_optional: r.is_optional, is_default: r.is_default,
      })),
    };
    try {
      if (isEdit) {
        await apiClient.put(`/plans/${plan.id}`, payload);
        toast.success("Plan updated");
      } else {
        await apiClient.post("/plans", payload);
        toast.success("Plan created");
      }
      onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save plan."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="plan-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Plan" : "New Plan"}</DialogTitle>
          <DialogDescription>Defines the plan's total duration, price, delivery days and which menu items are included.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Name" required value={form.name} onChange={setField("name")} error={errors.name} testId="plan-form-name" />
            <TextField label="Price" required value={form.price} onChange={setField("price")} error={errors.price} testId="plan-form-price" />
            <TextField label="Total Days" required value={form.quantity} onChange={setField("quantity")} error={errors.quantity} hint="How many deliveries this plan runs for." testId="plan-form-quantity" />
            <div className="space-y-1.5">
              <Label htmlFor="plan-form-status">Status</Label>
              <Select value={String(form.status)} onValueChange={(v) => setForm((f) => ({ ...f, status: Number(v) }))}>
                <SelectTrigger id="plan-form-status" data-testid="plan-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Active</SelectItem>
                  <SelectItem value="2">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="plan-form-description">Description</Label>
            <Textarea id="plan-form-description" data-testid="plan-form-description" rows={2} value={form.description} onChange={setField("description")} />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Items — Quantity per Delivery</Label>
                <p className="text-[11px] text-text-muted">e.g. Regular Tiffin: 1 Daal + 1 Sabzi + 1 Rice + 5 Rotis.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addItemRow} disabled={loadingDetail} data-testid="plan-form-add-item-button">
                <Plus size={13} strokeWidth={1.5} /> Add Item
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {items.length > 0 && (
                <div className="flex items-center gap-2 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  <span className="flex-1">Item</span>
                  <span className="w-16 text-center">Qty/Day</span>
                  <span className="w-[74px]">Default</span>
                  <span className="w-8" />
                </div>
              )}
              {items.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`plan-form-item-row-${idx}`}>
                  <Select value={row.item_id} onValueChange={(v) => setItemField(idx, "item_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`plan-form-item-select-${idx}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {itemOptions.map((it) => <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input
                    type="number" min="1" value={row.quantity} onChange={(e) => setItemField(idx, "quantity", e.target.value)}
                    className="h-9 w-16 rounded-md border border-border bg-surface px-2 text-sm text-text-main"
                    data-testid={`plan-form-item-qty-${idx}`}
                  />
                  <label className="flex items-center gap-1 text-xs text-text-muted" title="Included by default">
                    <Checkbox checked={row.is_default} onCheckedChange={(v) => setItemField(idx, "is_default", Boolean(v))} data-testid={`plan-form-item-default-${idx}`} /> Default
                  </label>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItemRow(idx)} data-testid={`plan-form-item-remove-${idx}`}>
                    <Trash2 size={14} strokeWidth={1.5} className="text-danger" />
                  </Button>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-text-muted">No items added yet.</p>}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="plan-form-cancel-button">Cancel</Button>
            <Button type="submit" variant="accent" disabled={saving || loadingDetail} data-testid="plan-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
